import { type ReactNode, useId } from 'react';
import { Badge, EmptyState } from '../components/ui';
import { brl, dataBr } from '../lib/format';
import { FUNDOS } from './Fundos';
import styles from './screens.module.css';

interface Categoria {
  categoria: string;
  total: number;
}
export interface Rascunho {
  identificacao: { condominio: string; cnpj: string | null; competencia: string };
  saldoInicial: number;
  receitas: { total: number; porCategoria: Categoria[] };
  despesas: { total: number; porCategoria: Categoria[] };
  resultado: number;
  saldoFinal: number;
  conciliacao: {
    saldoFinalCalculado: number;
    saldoFinalExtrato: number | null;
    diferenca: number | null;
    conciliado: boolean;
  };
  fundos: Array<{ tipoFundo: string; inicio: number; fim: number; variacao: number }>;
  rendaFixa: { inicio: number; fim: number; irIof: number; rendimentoLiquido: number } | null;
  inadimplencia: {
    previstoTotal: number;
    recebidoTotal: number;
    inadimplenteTotal: number;
    pctInadimplencia: number;
    unidadesPendentes: number[];
  };
  pendencias: {
    naoConciliados: Array<{ data: string; descricao: string; tipo: string; valor: number }>;
    ressalvas: string[];
  };
  secoes: Array<{ numero: number; titulo: string }>;
}

function Secao({
  numero,
  titulo,
  children,
}: { numero: number; titulo: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className={styles.stack}>
      <h3 id={id}>
        {numero}. {titulo}
      </h3>
      {children}
    </section>
  );
}

function PorCategoria({ itens }: { itens: Categoria[] }) {
  if (itens.length === 0) return <EmptyState>Nenhum lançamento conciliado.</EmptyState>;
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Categoria</th>
            <th className={styles.num}>Total</th>
          </tr>
        </thead>
        <tbody>
          {itens.map((i) => (
            <tr key={i.categoria}>
              <th scope="row">{i.categoria}</th>
              <td className={styles.num}>{brl(i.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** As 11 seções do rascunho (docs/13), na ordem do servidor; só formata, não calcula. */
export function Secoes({ rascunho: r }: { rascunho: Rascunho }) {
  const titulo = (n: number) => r.secoes.find((s) => s.numero === n)?.titulo ?? '';
  const { conciliacao: conc, inadimplencia: inad, pendencias } = r;
  return (
    <div className={styles.stack}>
      <Secao numero={1} titulo={titulo(1)}>
        <dl>
          <dt>Condomínio</dt>
          <dd>{r.identificacao.condominio}</dd>
          <dt>CNPJ</dt>
          <dd>{r.identificacao.cnpj ?? '—'}</dd>
          <dt>Competência</dt>
          <dd>{r.identificacao.competencia}</dd>
        </dl>
      </Secao>
      <Secao numero={2} titulo={titulo(2)}>
        <p>{brl(r.saldoInicial)}</p>
      </Secao>
      <Secao numero={3} titulo={titulo(3)}>
        <p>Total: {brl(r.receitas.total)}</p>
        <PorCategoria itens={r.receitas.porCategoria} />
      </Secao>
      <Secao numero={4} titulo={titulo(4)}>
        <p>Total: {brl(r.despesas.total)}</p>
        <PorCategoria itens={r.despesas.porCategoria} />
      </Secao>
      <Secao numero={5} titulo={titulo(5)}>
        <p>{brl(r.resultado)}</p>
      </Secao>
      <Secao numero={6} titulo={titulo(6)}>
        <p>{brl(r.saldoFinal)}</p>
      </Secao>
      <Secao numero={7} titulo={titulo(7)}>
        <p>Saldo final calculado: {brl(conc.saldoFinalCalculado)}</p>
        {conc.saldoFinalExtrato === null ? (
          <p className="muted">Sem extrato nesta competência.</p>
        ) : (
          <>
            <p>Saldo final do extrato: {brl(conc.saldoFinalExtrato)}</p>
            {conc.diferenca !== null ? <p>Diferença: {brl(conc.diferenca)}</p> : null}
            <Badge tone={conc.conciliado ? 'success' : 'warning'}>
              {conc.conciliado ? 'Conciliado' : 'Divergente'}
            </Badge>
          </>
        )}
      </Secao>
      <Secao numero={8} titulo={titulo(8)}>
        {r.fundos.length === 0 ? (
          <EmptyState>Nenhuma posição de fundo.</EmptyState>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Fundo</th>
                  <th className={styles.num}>Início</th>
                  <th className={styles.num}>Fim</th>
                  <th className={styles.num}>Variação</th>
                </tr>
              </thead>
              <tbody>
                {r.fundos.map((f) => (
                  <tr key={f.tipoFundo}>
                    <th scope="row">{FUNDOS[f.tipoFundo] ?? f.tipoFundo}</th>
                    <td className={styles.num}>{brl(f.inicio)}</td>
                    <td className={styles.num}>{brl(f.fim)}</td>
                    <td className={styles.num}>{brl(f.variacao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Secao>
      <Secao numero={9} titulo={titulo(9)}>
        {r.rendaFixa === null ? (
          <p className="muted">Sem posição de renda fixa.</p>
        ) : (
          <>
            <p>Início: {brl(r.rendaFixa.inicio)}</p>
            <p>Fim: {brl(r.rendaFixa.fim)}</p>
            <p>IR/IOF: {brl(r.rendaFixa.irIof)}</p>
            <p>Rendimento líquido: {brl(r.rendaFixa.rendimentoLiquido)}</p>
          </>
        )}
      </Secao>
      <Secao numero={10} titulo={titulo(10)}>
        <p>Previsto: {brl(inad.previstoTotal)}</p>
        <p>Recebido: {brl(inad.recebidoTotal)}</p>
        <p>Inadimplente: {brl(inad.inadimplenteTotal)}</p>
        <p>Inadimplência: {`${inad.pctInadimplencia.toFixed(2).replace('.', ',')}%`}</p>
        <p>Unidades pendentes: {inad.unidadesPendentes.length}</p>
      </Secao>
      <Secao numero={11} titulo={titulo(11)}>
        {pendencias.naoConciliados.length === 0 ? (
          <EmptyState>Nenhum lançamento pendente de conciliação.</EmptyState>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th>Data</th>
                  <th className={styles.num}>Valor</th>
                </tr>
              </thead>
              <tbody>
                {pendencias.naoConciliados.map((l) => (
                  <tr key={`${l.data}-${l.descricao}-${l.valor}`}>
                    <th scope="row">{l.descricao}</th>
                    <td>{dataBr(l.data)}</td>
                    <td className={styles.num}>{brl(l.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pendencias.ressalvas.length === 0 ? (
          <p className="muted">Nenhuma ressalva registrada.</p>
        ) : (
          <ul>
            {pendencias.ressalvas.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        )}
      </Secao>
    </div>
  );
}
